
import {
  Component, Emit, Prop, Vue, Watch,
} from 'nuxt-property-decorator'
import { globalLabelAsString } from '../../shared/general/services/StoreService'
import BaseButton from '../base/BaseButton.vue'
import { getURLParameterValue } from '../../shared/general/services/URLParameterService'
import { formatJoinFromToDate } from '../../shared/general/services/DateTimeService'

export interface ISearchResult {
  headline : string
  text : string
  link : string
  dateFrom : string
  dateTo : string
  eventLocation : string
}

@Component({
  name: 'SearchResults',
  components: {
    BaseButton,
    BaseLink: () => import('../base/BaseLink.vue'),
    SeparationLine: () => import('../SeparationLine.vue'),
  },
})
export default class SearchResults extends Vue {
  @Prop({ default: () => [] }) searchResults! : ISearchResult[]

  @Prop({ required: true }) numberOfTotalResults! : number

  @Prop({ default: false }) showLoadingAnimation! : boolean

  @Prop({ default: false }) showNoSearchResultsLabel! : boolean

  private currentQuery : string = ''

  created () {
    this.updateCurrentQuery()
  }

@Watch('searchResults')
  onSearchResultsChanged () {
    this.updateCurrentQuery()
  }

private formatDate (from : string, to : string) : string {
  const dateOptions : Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' }
  return formatJoinFromToDate({ from, to, dateOptions })
}

private get showLoadMoreButton () {
  return this.searchResults.length && (this.numberOfTotalResults > this.searchResults.length)
}

private updateCurrentQuery () {
  this.currentQuery = getURLParameterValue('q') || ''
}

private get amountOfResults () : string {
  const createLabelFor = (label : string, showNumberOfResults : boolean) : string => {
    if (showNumberOfResults) return `${this.numberOfTotalResults} ${label} "${this.currentQuery}"`
    return `${label} "${this.currentQuery}"`
  }

  if (this.showNoSearchResultsLabel) return createLabelFor(this.noSearchResultsFoundForLabel, false)
  if (this.numberOfTotalResults === 1) return createLabelFor(this.singleResultForLabel, true)
  return createLabelFor(this.resultsForLabel, true)
}

/**
   * number of results > 1
   */
private get resultsForLabel () : string {
  return globalLabelAsString('results_for_label')
}

/**
   * number of results = 1
   */
private get singleResultForLabel () : string {
  return globalLabelAsString('single_result_for_label')
}

/**
   * number of results = 0
   */
private get noSearchResultsFoundForLabel () : string {
  return globalLabelAsString('no_search_results_found_for_label')
}

private get loadMoreLabel () : string {
  return globalLabelAsString('load_more_label')
}

private formatLink (link : string) : string {
  const path = link.startsWith('/') ? link : `/${link}`
  return `${window.location.protocol}//${window.location.host}${path}`
}

private sanitizeTags (value ?: string) : string | undefined {
  // Matches any tag which is not <br> or <strong>, </br> or </strong>
  return value?.replaceAll(/<\/?(?!(?:br|strong)\b)\w(?:[^>"']|"[^"]*"|'[^']*')*>/g, '')
}

  @Emit('load-more-search-results')
private loadMoreSearchResults () : void {}
}
